import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";
import { FormGroup, Label, Button, ModalFooter } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import axios from "../../services/api/api";
import Select from "react-select";
import { AUTH_BASE_URL } from "../../constants/api.constants";
import { checkNameLengthAndCharacters } from "../../Utilities/checkerFunctions";

const EditStudentForm = ({
  values,
  errors,
  touched,
  isSubmitting,
  handleChange,
}) => {
  const intl = useIntl();
  const handleClassChange = (c) => {
    values.selectedClass = c;
  };

  return (
    <div className="col-12 col-sm-12">
      <Form>
        <FormGroup>
          <Label>
            <FormattedMessage id="Name" />
          </Label>
          <Field
            className={`form-control ${
              errors.studentName && touched.studentName && "is-invalid"
            }`}
            type="input"
            name="studentName"
            value={values.studentName}
            onChange={handleChange}
          />
          {errors.studentName && touched.studentName && (
            <div className="invalid-feedback">{errors.studentName}</div>
          )}
        </FormGroup>
        <FormGroup>
          <Label>
            <FormattedMessage id="EmailAddress" />
          </Label>
          <Field
            className={`form-control ${
              errors.newEmail && touched.newEmail && "is-invalid"
            }`}
            type="email"
            name="newEmail"
            value={values.newEmail}
            onChange={handleChange}
          />
          {errors.newEmail && touched.newEmail && (
            <div className="invalid-feedback">{errors.newEmail}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="exampleSelectMulti">
            <FormattedMessage id="ChooseClass" />
          </Label>
          <Select
            placeholder={intl.formatMessage({ id: "ChooseClasses" })}
            className={`${
              errors.selectedClasses && touched.selectedClasses && "is-invalid"
            }`}
            noOptionsMessage={() =>
              intl.formatMessage({ id: "NoMoreSelectableClassesForStudents" })
            }
            onChange={(e) => handleClassChange(e)}
            defaultValue={values.currentClass}
            options={values.classes}
          />

          {errors.selectedClasses && touched.selectedClasses && (
            <div className="invalid-feedback">{errors.selectedClasses}</div>
          )}
        </FormGroup>
        <ModalFooter>
          <Button
            outline
            color="secondary"
            type="button"
            onClick={values.dismissModal}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button type="submit" outline color="primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <FormattedMessage id="Wait" />
              </>
            ) : (
              <>
                <FormattedMessage id="Save" />
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </div>
  );
};

const FormikForm = withFormik({
  mapPropsToValues: (props) => {
    if (!props.student || !props.student.hasOwnProperty("name")) return;

    return {
      classes: props.classes
        ? props.classes.map((c) => {
            return { label: c.name, value: c.id };
          })
        : [],
      selectedClass: {
        value: props.student.class.id,
        label: props.student.class.name,
      },
      dismissModal: props.dismissModal,
      studentName: props.student.name,
      newEmail: props.student.email,
      oldEmail: props.student.email,
      currentClass: {
        value: props.student.class.id,
        label: props.student.class.name,
      },
    };
  },

  validationSchema: Yup.object().shape({
    newEmail: Yup.string()
      .email(
        <FormattedMessage id="Views.Teacher.CreateStudentForm.Error.RequireEmail" />
      )
      .required(
        <FormattedMessage id="Views.Teacher.CreateStudentForm.Error.RequireEmail" />
      ),
    studentName: Yup.string().required(
      <FormattedMessage id="Views.Teacher.CreateStudentForm.Error.RequireName" />
    ),
  }),

  updateStudent(values, setSubmitting, props) {
    if (checkNameLengthAndCharacters(values.studentName) !== true) {
      setSubmitting(false);
      toast.warn(checkNameLengthAndCharacters(values.studentName));
    } else {
      axios
        .put("/user/UpdateUser", {
          name: values.studentName,
          oldEmail: values.oldEmail,
          newEmail: values.newEmail,
        })
        .then(() => {
          this.updateStudentClass(values, setSubmitting, props);
        })
        .catch((err) => {
          toast.warn(err?.response?.data ?? <FormattedMessage id="UserUpdateFailed" />);
          setSubmitting(false);
        });
    }
  },

  updateStudentClass(values, setSubmitting, props) {
    axios
      .post("/Student/ChangeClass", {
        id: props.student.id,
        name: props.student.name,
        class: {
          name: values.selectedClass.label,
          id: values.selectedClass.value,
          schoolId: props.student.class.schoolId,
        },
      })
      .then((response) => {
        toast.success("Eleven har uppdaterats");
        setSubmitting(false);
        if (props.refreshStudent) {
          props.refreshStudent();
        } else if (props.reloadStudents) {
          props.reloadStudents(response.data.result);
        }
        values.dismissModal();
      })
      .catch((err) => {
        toast.warn(err?.response?.data ?? <FormattedMessage id="UserUpdateFailed" />);
        setSubmitting(false);
      });
  },

  handleSubmit(values, { props, setErrors, setSubmitting }) {
    if (values.oldEmail !== values.newEmail) {
      axios
        .post(
          `${AUTH_BASE_URL}/token/IsEmailTaken/?email=${encodeURIComponent(
            values.newEmail
          )}`
        )
        .then((response) => {
          if (response.data === false) {
            this.updateStudent(values, setSubmitting, props);
          } else {
            setErrors({ newEmail: <FormattedMessage id="EmailAddressTaken" /> });
            setSubmitting(false);
          }
        })
        .catch((err) => {
          setErrors({ newEmail: err.response.data });
          setSubmitting(false);
        });
    } else {
      this.updateStudent(values, setSubmitting, props);
    }
  },
})(EditStudentForm);

export default FormikForm;
